import React, { memo, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box } from '@atlaskit/primitives/compiled';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ItemWithEntryPointPopup } from '@atlassian/jira-item-with-popup/src/ui/item-with-entrypoint-popup/index.tsx';
import { TagItem } from '@atlassian/jira-list-with-popup/src/ui/tag-item/index.tsx';
import type { IssueLinksCell$key } from '@atlassian/jira-relay/src/__generated__/IssueLinksCell.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';
import { issueLinksPopUpContentEntryPoint } from './issue-links-popup-content/entrypoint.tsx';
import messages from './messages.tsx';

export type Props = {
	fieldRef: IssueLinksCell$key;
	issueSearchBaseUrl: string;
	issueKey: string;
	/**
	 * Whether the accordions should be open by default when opening the ListCellWithPopup
	 * Default = false
	 */
	accordionInitialIsOpen?: boolean;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export type IssueLink = {
	key: string;
	name: string;
	href: string;
	relationName?: string | null;
	shouldDisplayStrikethrough: boolean;
};

export const IssueLinksCell = memo<Props>(
	({
		fieldRef,
		issueSearchBaseUrl,
		issueKey,
		accordionInitialIsOpen = false,
		isReadOnlyWithoutHoverPopover,
	}: Props) => {
		const { formatMessage } = useIntl();

		const data = useFragment<IssueLinksCell$key>(
			graphql`
				fragment IssueLinksCell on JiraIssueLinkField
				@argumentDefinitions(
					isNinStrikethroughResolutionUpdateEnabled: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/nin-resolution-strikethough-enabled.relayprovider"
					}
				) {
					issueLinkConnection(first: 1) {
						totalCount
						edges {
							node {
								issue {
									webUrl
									key
									fieldsById(ids: ["statusCategory"]) {
										edges {
											node {
												... on JiraStatusCategoryField {
													statusCategory {
														statusCategoryId
													}
												}
											}
										}
									}
									isResolved @include(if: $isNinStrikethroughResolutionUpdateEnabled)
								}
								relationName
							}
						}
					}
				}
			`,
			fieldRef,
		);

		const cloudId = useCloudId();
		const entryPointParams = useMemo(() => ({ issueKey, cloudId }), [issueKey, cloudId]);

		const issueLinks = data?.issueLinkConnection?.edges;
		const totalCount = data?.issueLinkConnection?.totalCount;

		const filteredIssueLinks: IssueLink[] = useMemo(
			() =>
				issueLinks
					?.map((edge) =>
						edge?.node?.issue?.key != null
							? {
									key: edge.node.issue?.key || 'missingId',
									name: edge.node.issue?.key,
									href: edge.node.issue?.webUrl || '',
									relationName: edge.node.relationName,
									shouldDisplayStrikethrough: fg('nin-strikethrough-resolution-update')
										? edge.node.issue?.isResolved === true
										: edge.node.issue?.fieldsById?.edges?.[0]?.node?.statusCategory
												?.statusCategoryId === '3' || false,
								}
							: null,
					)
					.filter(Boolean) ?? [],
			[issueLinks],
		);

		const isSingleLineRowHeightEnabled =
			useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

		const runtimeProps = useMemo(
			() => ({ issueKey, issueSearchBaseUrl, accordionInitialIsOpen }),
			[issueKey, issueSearchBaseUrl, accordionInitialIsOpen],
		);

		if (!issueLinks) {
			return <ErrorCell cellType="issueLinks" reason="Missing issue links field" />;
		}
		if (issueLinks.length !== filteredIssueLinks.length) {
			return <ErrorCell cellType="issueLinks" reason="Missing fields on issue links" />;
		}
		if (totalCount === undefined || totalCount === null) {
			return <ErrorCell cellType="issueLinks" reason="Missing total count" />;
		}
		if (totalCount < 0) {
			return <ErrorCell cellType="issueLinks" reason="Invalid total count" />;
		}
		if (totalCount > 0 && filteredIssueLinks.length === 0) {
			return (
				<ErrorCell cellType="issueLinks" reason="Total count valid but no issue links given" />
			);
		}

		return (
			<Box>
				<ItemWithEntryPointPopup
					entryPoint={issueLinksPopUpContentEntryPoint}
					entryPointParams={entryPointParams}
					entryPointRuntimeProps={runtimeProps}
					totalItems={Math.min(totalCount, 1000)}
					ItemComponent={
						totalCount ? (
							<TagItem
								name={filteredIssueLinks[0].name}
								href={filteredIssueLinks[0].href}
								shouldDisplayStrikethrough={filteredIssueLinks[0].shouldDisplayStrikethrough}
								key={filteredIssueLinks[0].key}
								isHoverPopoverEnabled={isSingleLineRowHeightEnabled}
								isOnlyOneItem={totalCount === 1}
							/>
						) : undefined
					}
					showMoreTooltip={formatMessage(messages.triggerButton)}
					actionSubjectIdPrefix="issueLinks"
					isTruncated={isSingleLineRowHeightEnabled}
				/>
			</Box>
		);
	},
);
